import React, { ChangeEvent, forwardRef, ReactNode, useCallback, useState } from 'react';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';

import Tooltip from './Tooltip/Tooltip';
import Icon, { IconEnum } from './Icon';

const FilterIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .icon {
        width: 16px;
        height: 16px;
    }

    img {
        width: 16px;
        height: 16px;
    }
`;

const Container = styled.span`
    position: relative;
    display: flex;
    gap: 10px;
    flex-direction: column;
    padding-bottom: 0;
    ${(props) => props.inputColumnPosition ? `grid-column: ${props.inputColumnPosition};` : ''};

    & > span:has(> input),
    & > div > span:has(> input),
    & > span > span:has(> input) {
        height: 40px;
        padding: 6px 10px 6px 16px;
    }
`;

const InputTitle = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    padding: 0 10px;
    width: 100%;
    color: var(--color-primary);
    font-weight: var(--font-weight);
    font-size: var(--font-size-body-2);
    line-height: 0;

    @media (max-width: 1475px) {
        line-height: 1;
    }
`;

const Title = styled.div`
    font-size: var(--font-size-body-2);
    display: flex;
    align-items: center;
`;

const Action = styled.div<{ isBlocked?: boolean }>`
    font-style: normal;
    font-weight: var(--font-weight);
    font-size: var(--font-size-body-3);
    line-height: 150%;
    text-decoration-line: underline;
    color: var(--color-primary-70);
    cursor: ${(props) => props.isBlocked ? 'not-allowed' : 'pointer'};
    margin-left: auto;
    opacity: ${(props) => props.isBlocked ? 0.5 : 1};
    pointer-events: ${(props) => props.isBlocked ? 'none' : 'auto'};

    &:hover {
        color: var(--color-primary);
    }

    text-align: right;
`;

const ActionCheckbox = styled.div`
    display: flex;
    gap: 5px;
    align-items: center;
    margin-left: auto;

    input:checked + label {
        color: var(--color-primary);
    }

    input[type="checkbox"] {
        --checkbox-color: var(--color-primary-70);

        width: 14px;
        height: 14px;
    }

    input[type="checkbox"]:checked::before {
        width: 2px;
        height: 5px;
    }

    input[type="checkbox"]:checked {
        --checkbox-color: var(--color-primary);
    }

    label {
        color: var(--color-primary-70);
        font-size: 12px;
        text-decoration-line: underline;
        text-align: right;
    }
`;

const HelperWrapper = styled.div`
    .icon {
        margin-left: 5px;
    }
`;

const AdditionalIconButton = styled.div`
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 25px;
    padding: 12px;

    img {
        filter: ${(props) => props.darkMode ? 'invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%)' : 'unset'};
    }


    .icon {
        width: 20px;
        height: 20px;
    }

    &:hover {
        opacity: 0.8;
    }

    span {
        font-size: 16px;
    };
`;

const Star = styled.span`
    color: var(--color-error);
    margin-left: 5px;
`;

const AdditionalWrapper = styled.span`
    margin-left: auto;
    font-size: var(--font-size-body-3);
    color: var(--color-primary-70);
    padding-left: 40px;
`;

const VisibleHelperText = styled.p`
    font-wight: 400;
    font-size: var(--font-size-body-3);
    padding: 10px
`;

const SubtitleText = styled.span`
    font-size: 11px;
    font-weight: 400;
    opacity: 0.65;
    line-height: 14px;
    padding: 0 10px;
    color: var(--color-primary);
`;

export interface AdditionalIconButtonProperties {
    textHelper: string,
    handleClick: () => void,
    icon: IconEnum,
    iconText?: string
}

interface InputContainerProperties {
    icon?: IconEnum,
    title: string,
    actionText?: string,
    helperText?: string,
    subtitleText?: string,
    actionCheckbox?: boolean,
    onActionClick?: (e?: ChangeEvent<HTMLInputElement>) => void,
    onMouseEnter?: () => void,
    onMouseLeave?: () => void,
    children: ReactNode,
    checked?: boolean,
    className?: string,
    required?: boolean
    additionalText?: string;
    inputColumnPosition?: string;
    visibleHelperText?: string;
    additionalIconButton?: AdditionalIconButtonProperties,
    isDuplicateFilterButton?: boolean;
}
const InputContainer = forwardRef<HTMLSpanElement, InputContainerProperties>(({
    children,
    title,
    icon,
    onActionClick,
    actionText,
    onMouseEnter,
    onMouseLeave,
    actionCheckbox,
    checked,
    helperText,
    subtitleText,
    className,
    required,
    additionalText,
    inputColumnPosition,
    visibleHelperText,
    additionalIconButton,
    isDuplicateFilterButton = false
}, ref) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [referenceElement, setReferenceElement] = useState(null);

    const [showIconTooltip, setShowIconTooltip] = useState(false);
    const [iconReferenceElement, setIconReferenceElement] = useState(null);
    const { user } = useUser();

    const [isActionBlocked, setIsActionBlocked] = useState(false);

    const handleActionClick = useCallback((e?: ChangeEvent<HTMLInputElement>) => {
        if (isDuplicateFilterButton && isActionBlocked) {
            return;
        }

        if (isDuplicateFilterButton) {
            setIsActionBlocked(true);
            setTimeout(() => {
                setIsActionBlocked(false);
            }, 1000);
        }

        onActionClick?.(e);
    }, [
        isDuplicateFilterButton,
        isActionBlocked,
        onActionClick
    ]);

    return (
        <Container inputColumnPosition={inputColumnPosition} className={className} ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <InputTitle>
                {icon && <FilterIconWrapper><Icon icon={icon}/></FilterIconWrapper>}
                <Title>
                    {title}
                    {helperText &&
                        <HelperWrapper onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                            ref={setReferenceElement}>
                            <Icon icon={IconEnum.QUESTION_CIRCLE}/>
                            <Tooltip text={helperText} referenceElement={referenceElement} showTooltip={showTooltip}/>
                        </HelperWrapper>
                    }
                    {
                        required && <Star>*</Star>
                    }
                    {additionalIconButton &&
                        <HelperWrapper
                            onMouseEnter={() => setShowIconTooltip(true)}
                            onMouseLeave={() => setShowIconTooltip(false)}
                            ref={setIconReferenceElement}>
                            <AdditionalIconButton onClick={additionalIconButton.handleClick} darkMode={user.isDarkMode}>
                                <Icon icon={additionalIconButton.icon}/>
                                {additionalIconButton.iconText && <span>{additionalIconButton.iconText}</span>}
                            </AdditionalIconButton>
                            <Tooltip
                                text={additionalIconButton.textHelper}
                                referenceElement={iconReferenceElement}
                                showTooltip={showIconTooltip}
                            />
                        </HelperWrapper>
                    }
                </Title>
                {additionalText && <AdditionalWrapper>{additionalText}</AdditionalWrapper>}
                {actionText && !actionCheckbox && <Action onClick={handleActionClick} isBlocked={isDuplicateFilterButton && isActionBlocked}>{actionText}</Action>}
                {actionText && actionCheckbox &&
                    <ActionCheckbox>
                        <input onChange={onActionClick}
                            checked={checked}
                            type="checkbox"
                            id={actionText.replace(/\s+/g, '')}/>
                        <label htmlFor={actionText.replace(/\s+/g, '')}>{actionText}</label>
                    </ActionCheckbox>}
            </InputTitle>
            {children}
            {subtitleText && <SubtitleText>{subtitleText}</SubtitleText>}
            {visibleHelperText && <VisibleHelperText>{`* ${visibleHelperText}`}</VisibleHelperText>}
        </Container>
    );
});

export default InputContainer;
